package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.hiring.InterviewDto
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.domain.model.CandidateMail
import com.evoapps.evoshift.domain.model.Interview
import com.evoapps.evoshift.domain.model.InterviewClash
import com.evoapps.evoshift.domain.model.InterviewRequest
import com.evoapps.evoshift.domain.model.InterviewResult
import com.evoapps.evoshift.domain.model.InterviewStatus
import com.evoapps.evoshift.domain.repository.InterviewRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.add
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray

/**
 * SH-210 interviews in Firestore (`interviews/{id}`, read only) and the scheduleInterview and
 * cancelInterview callables (functions/hiringPipeline.js), which write them and e-mail the
 * candidate.
 */
class InterviewRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : InterviewRepository {

    private val interviews get() = firestore.collection("interviews")

    private fun dev.gitlive.firebase.firestore.QuerySnapshot.toInterviews(): List<Interview> =
        documents.mapNotNull { doc -> runCatching { toDomain(doc.data<InterviewDto>(), doc.id) }.getOrNull() }
            .sortedBy { it.start }

    override fun observeForApplication(postId: String, applicationId: String): Flow<List<Interview>> =
        interviews.where { "postId" equalTo postId }.where { "applicationId" equalTo applicationId }
            .safeSnapshots.map { it.toInterviews() }.distinctUntilChanged()

    /** The roster's interviews; the rules need the workplace in the query (managers read them by it). */
    private fun ofRoster(rosterId: String) = interviews
        .where { "workplaceId" equalTo com.evoapps.evoshift.domain.model.RosterIds.workplaceIdOf(rosterId) }
        .where { "rosterId" equalTo rosterId }

    override fun observeForRoster(rosterId: String): Flow<List<Interview>> =
        ofRoster(rosterId)
            .safeSnapshots.map { it.toInterviews() }
            // Not everyone who opens a roster may read its interviews: none then.
            .catch { emit(emptyList()) }
            .distinctUntilChanged()

    override suspend fun getForRoster(rosterId: String): Result<List<Interview>> = runCatching {
        ofRoster(rosterId).get().toInterviews()
    }

    override fun observeForParticipant(userId: String, fromDate: String): Flow<List<Interview>> =
        interviews.where { "participantIds" contains userId }
            .safeSnapshots.map { snapshot -> snapshot.toInterviews().filter { it.date >= fromDate } }
            .catch { emit(emptyList()) }
            .distinctUntilChanged()

    override suspend fun schedule(request: InterviewRequest): Result<InterviewResult> = runCatching {
        val answer = functions.call("scheduleInterview", buildJsonObject {
            put("postId", request.postId)
            put("applicationId", request.applicationId)
            request.interviewId?.let { put("interviewId", it) }
            put("startsAt", request.startsAt)
            put("durationMinutes", request.durationMinutes)
            request.location?.let { put("location", it) }
            request.onlineUrl?.let { put("onlineUrl", it) }
            request.note?.let { put("note", it) }
            putJsonArray("participantIds") { request.participantIds.forEach { add(it) } }
            put("paid", request.paid)
            put("sendInvitation", request.sendInvitation)
        }).jsonObject
        InterviewResult(
            interviewId = answer.text("interviewId") ?: error("No interview id"),
            sequence = answer["sequence"]?.jsonPrimitive?.intOrNull ?: 0,
            mail = mailOf(answer.text("mail")),
            clashes = (answer["clashes"]?.jsonArray ?: emptyList<JsonElement>()).mapNotNull { element ->
                val clash = element as? JsonObject ?: return@mapNotNull null
                InterviewClash(
                    userId = clash.text("userId") ?: return@mapNotNull null,
                    start = clash.text("start").orEmpty(),
                    end = clash.text("end").orEmpty(),
                    isInterview = clash["isInterview"]?.jsonPrimitive?.booleanOrNull == true
                )
            }
        )
    }

    override suspend fun cancel(interviewId: String, notify: Boolean): Result<CandidateMail> = runCatching {
        val answer = functions.call("cancelInterview", buildJsonObject {
            put("interviewId", interviewId)
            put("notify", notify)
        }).jsonObject
        mailOf(answer.text("mail"))
    }

    companion object {
        private fun JsonObject.text(key: String): String? = this[key]?.jsonPrimitive?.contentOrNull

        fun mailOf(value: String?): CandidateMail = CandidateMail.entries.firstOrNull { it.name == value } ?: CandidateMail.NOT_REQUESTED

        fun toDomain(dto: InterviewDto, id: String): Interview = Interview(
            id = id,
            workplaceId = dto.workplaceId,
            rosterId = dto.rosterId,
            date = dto.date,
            start = dto.start,
            end = dto.end,
            durationMinutes = dto.durationMinutes,
            postId = dto.postId,
            applicationId = dto.applicationId,
            candidateName = dto.candidateName?.takeIf { it.isNotBlank() },
            jobTitle = dto.jobTitle,
            location = dto.location?.takeIf { it.isNotBlank() },
            onlineUrl = dto.onlineUrl?.takeIf { it.isNotBlank() },
            note = dto.note?.takeIf { it.isNotBlank() },
            participantIds = dto.participantIds,
            paid = dto.paid,
            status = InterviewStatus.fromId(dto.status),
            sequence = dto.sequence,
            createdAt = dto.createdAt,
            createdBy = dto.createdBy,
            updatedAt = dto.updatedAt,
            updatedBy = dto.updatedBy
        )
    }
}
